import axios from "axios";
import { store } from "../app/store";
import { logout, setCredentials } from "../features/auth/authSlice";
import { notifyRequestFailure } from "../utils/formErrors";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api" || "https://uat-aucrm.devshow.in/";

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // the refresh token lives in an HttpOnly cookie that the browser sends by itself
});

// ─── Request Interceptor ─────────────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const state = store.getState();
    const token = state.auth?.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response Interceptor (auto-refresh JWT) ─────────────────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const state = store.getState();

      try {
        // the refresh token is in the cookie. People who were signed in before the cookie existed still have one in
        // localStorage: send it once, the server then puts the new token into the cookie, and we delete the old copy.
        let legacy = null;
        try { legacy = localStorage.getItem("refresh_token"); } catch { /* storage blocked */ }
        const response = await axios.post(`${BASE_URL}/auth/refresh/`, legacy ? { refresh: legacy } : {}, { withCredentials: true });
        try { localStorage.removeItem("refresh_token"); } catch { /* storage blocked */ }

        const { access } = response.data;
        store.dispatch(setCredentials({ ...state.auth, token: access }));
        processQueue(null, access);
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        store.dispatch(logout());
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    notifyRequestFailure(error);
    return Promise.reject(error);
  }
);

export default api;

// ─── API Helpers ─────────────────────────────────────────────────────────────
export const buildQueryString = (params = {}) => {
  const filtered = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  return filtered.length ? `?${filtered.join("&")}` : "";
};
